/**
 * useWebSocket - Real-time WebSocket hook for messaging
 * Phase 11 Part 2: WebSocket-based real-time messaging
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;
const PING_INTERVAL = 30000;

export function useWebSocket(url, options = {}) {
  const {
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    autoReconnect = true,
    enabled = true
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  // Store callbacks in refs to avoid dependency issues
  const onMessageRef = useRef(onMessage);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onMessageRef.current = onMessage;
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
    onErrorRef.current = onError;
  }, [onMessage, onConnect, onDisconnect, onError]);

  const disconnect = useCallback(() => {
    clearInterval(pingIntervalRef.current);
    clearTimeout(reconnectTimeoutRef.current);
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!url || !enabled || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WebSocket] Connected');
        setIsConnected(true);
        setReconnectAttempts(0);
        reconnectAttemptsRef.current = 0;
        onConnectRef.current?.();

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Ignore pong messages
          if (data.type !== 'pong') {
            onMessageRef.current?.(data);
          }
        } catch (e) {
          console.error('[WebSocket] Failed to parse message:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        setIsConnected(false);
        clearInterval(pingIntervalRef.current);
        onDisconnectRef.current?.(event);

        // Auto-reconnect logic
        if (autoReconnect && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS && enabled) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[WebSocket] Attempting reconnect...');
            reconnectAttemptsRef.current += 1;
            setReconnectAttempts(reconnectAttemptsRef.current);
            // Create new connection
            if (wsRef.current === null || wsRef.current.readyState === WebSocket.CLOSED) {
              const newWs = new WebSocket(url);
              wsRef.current = newWs;
              
              newWs.onopen = ws.onopen;
              newWs.onmessage = ws.onmessage;
              newWs.onclose = ws.onclose;
              newWs.onerror = ws.onerror;
            }
          }, RECONNECT_DELAY);
        }
      };

      ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        onErrorRef.current?.(error);
      };
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      onErrorRef.current?.(error);
    }
  }, [url, enabled, autoReconnect]);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  const sendMessage = useCallback((text, attachments = []) => {
    return send({
      type: 'send_message',
      text,
      attachments
    });
  }, [send]);

  const sendTyping = useCallback((isTyping) => {
    return send({
      type: 'typing',
      is_typing: isTyping
    });
  }, [send]);

  const markRead = useCallback(() => {
    return send({ type: 'mark_read' });
  }, [send]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (enabled && url) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [url, enabled, connect, disconnect]);

  return {
    isConnected,
    reconnectAttempts,
    send,
    sendMessage,
    sendTyping,
    markRead,
    connect,
    disconnect
  };
}

/**
 * Build WebSocket URL for chat
 */
export function buildChatWsUrl(conversationId, token) {
  if (!conversationId || !token) return null;
  
  // Get base URL from environment or current location
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Convert HTTP to WS protocol
  let wsBase = backendUrl
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
  
  // If no backend URL, use current host
  if (!wsBase) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    wsBase = `${protocol}//${window.location.host}`;
  }
  
  return `${wsBase}/ws/chat/${conversationId}?token=${token}`;
}

/**
 * Build WebSocket URL for support ticket
 */
export function buildSupportWsUrl(ticketId, token) {
  if (!ticketId || !token) return null;
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  let wsBase = backendUrl
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
  
  if (!wsBase) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    wsBase = `${protocol}//${window.location.host}`;
  }
  
  return `${wsBase}/ws/support/${ticketId}?token=${token}`;
}

export default useWebSocket;
